'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@quadminds/mindkit';
import { RiLoader4Line } from '@remixicon/react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FileItem } from '../../../../../types/File/File.types';

interface DeleteConfirmationDialogProps {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onConfirm: () => void;
    readonly items: FileItem[] | null;
    readonly isLoding: boolean;
}

export function DeleteConfirmationDialog({
    isOpen,
    onClose,
    onConfirm,
    items,
    isLoding,
}: DeleteConfirmationDialogProps) {
    const { t } = useTranslation();

    if (!items || items.length === 0) return null;

    const isSingleItem = items.length === 1;
    const item = items[0];
    const isSingleFolder = isSingleItem && item.type === 'folder';

    const getTitle = () => {
        if (isSingleItem) {
            return t(`modals.deleteFile.${item.type === 'folder' ? 'title1' : 'title2'}`);
        }
        return t('modals.deleteFile.title3');
    };

    const getMessage = () => {
        if (isSingleItem) {
            if (isSingleFolder) {
                return t('modals.deleteFile.description1', { name: item.name });
            }
            return t('modals.deleteFile.description2', { name: item.name });
        }
        return t('modals.deleteFile.description3', { length: items.length });
    };

    const handleConfirm = () => {
        onConfirm();
    };
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-full max-w-lg">
                <DialogHeader>
                    <div className="flex items-center gap-2 text-destructive">
                        <AlertTriangle className="h-5 w-5" />
                        <DialogTitle data-testid="delete-dialog-title">{getTitle()}</DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">{getMessage()}</DialogDescription>
                </DialogHeader>
                {isSingleItem && (
                    <div className="py-4">
                        <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
                            <span className="font-medium break-words">{item.name}</span>
                            {item.type === 'folder' && <span className="text-xs text-muted-foreground">(carpeta)</span>}
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isLoding}>
                        {t('modals.common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={isLoding}
                        data-testid="delete-dialog-submit"
                    >
                        {isLoding ? <RiLoader4Line className="animate-spin" /> : t('modals.deleteFile.delete')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteConfirmationDialog;
